import { normalizeMessage } from '../../conversations/services/conversationService';
import { Message } from '../../conversations/types/Conversation';
import {
  WebWidgetAppearance,
  WebWidgetBootstrapRequest,
  WebWidgetBootstrapResponse,
  WebWidgetConversationState,
  WebWidgetRefreshSessionResponse,
  WebWidgetRevokeSessionResponse,
  WebWidgetSession,
  WebWidgetSessionAttachmentMessageRequest,
  WebWidgetSessionMessageRequest,
  WebWidgetSessionMessageResponse,
  WebWidgetSessionMessagesPage,
  WebWidgetSessionMessagesQuery,
  WebWidgetSessionState,
  WebWidgetVisitorIdentitySettings,
  WebWidgetVisitorState,
} from '../types/webWidget';
import { normalizeTypingIndicators } from '../utils/webWidgetTypingIndicators';
import {
  clearStoredWebWidgetSession,
  readStoredWebWidgetSession,
  writeStoredWebWidgetSession,
} from '../utils/webWidgetSessionStorage';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const WEB_WIDGET_BASE_PATH = '/api/v1/web-widget';
const WEB_WIDGET_TENANT_HEADER = 'X-Web-Widget-Tenant-Code';
const WEB_WIDGET_KEY_HEADER = 'X-Web-Widget-Key';

type FetchLike = typeof fetch;

interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  formData?: FormData;
}

export interface WebWidgetClientConfig {
  tenantCode: string;
  widgetKey: string;
  baseUrl?: string;
  fetch?: FetchLike;
  origin?: string;
  sessionToken?: string;
}

export interface WebWidgetClient {
  bootstrap(request: WebWidgetBootstrapRequest): Promise<WebWidgetBootstrapResponse>;
  getSessionState(): Promise<WebWidgetSessionState>;
  getSessionMessages(query: WebWidgetSessionMessagesQuery): Promise<WebWidgetSessionMessagesPage>;
  sendSessionMessage(request: WebWidgetSessionMessageRequest): Promise<WebWidgetSessionMessageResponse>;
  sendSessionAttachment(request: WebWidgetSessionAttachmentMessageRequest): Promise<WebWidgetSessionMessageResponse>;
  refreshSession(): Promise<WebWidgetRefreshSessionResponse>;
  revokeSession(): Promise<WebWidgetRevokeSessionResponse>;
  setSessionToken(token: string | null): void;
  getSessionToken(): string | null;
}

const ensureFetch = (fetchImpl?: FetchLike): FetchLike => {
  if (fetchImpl) {
    return fetchImpl;
  }

  if (typeof globalThis.fetch === 'function') {
    return globalThis.fetch.bind(globalThis);
  }

  throw new Error('Fetch API is not available in this environment.');
};

const normalizeOptionalString = (value: unknown): string | undefined => {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
};

const normalizeRequiredString = (value: unknown): string => {
  return typeof value === 'string' ? value.trim() : '';
};

const normalizeIsoTimestamp = (value: unknown, fallback?: string): string => {
  const fallbackValue = fallback || new Date(0).toISOString();
  if (typeof value !== 'string' || !value.trim()) {
    return fallbackValue;
  }

  const raw = value.trim();
  const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(raw);
  const candidate = hasTimezone ? raw : `${raw}Z`;
  const parsed = new Date(candidate);
  return Number.isNaN(parsed.getTime()) ? fallbackValue : parsed.toISOString();
};

const normalizeBoolean = (value: unknown): boolean => Boolean(value);

const normalizeOptionalNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
};

const normalizeAppearance = (value: unknown): WebWidgetAppearance => {
  const appearance = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    primaryColor: normalizeOptionalString(appearance.primaryColor),
    launcherLabel: normalizeOptionalString(appearance.launcherLabel),
    brandName: normalizeOptionalString(appearance.brandName),
  };
};

const normalizeVisitorIdentity = (value: unknown): WebWidgetVisitorIdentitySettings => {
  const identity = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    requireEmail: normalizeBoolean(identity.requireEmail),
    requirePhone: normalizeBoolean(identity.requirePhone),
  };
};

const normalizeSession = (value: unknown): WebWidgetSession => {
  const session = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    id: normalizeRequiredString(session.id),
    token: normalizeOptionalString(session.token),
    expiresAtUtc: normalizeIsoTimestamp(session.expiresAtUtc),
  };
};

const normalizeVisitor = (value: unknown): WebWidgetVisitorState => {
  const visitor = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    id: normalizeRequiredString(visitor.id),
    displayName: normalizeOptionalString(visitor.displayName),
    externalVisitorId: normalizeOptionalString(visitor.externalVisitorId),
    email: normalizeOptionalString(visitor.email),
    phone: normalizeOptionalString(visitor.phone),
  };
};

const normalizeConversation = (value: unknown): WebWidgetConversationState | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const conversation = value as Record<string, unknown>;
  return {
    id: normalizeRequiredString(conversation.id),
    title: normalizeOptionalString(conversation.title),
    createdAt: normalizeIsoTimestamp(conversation.createdAt),
    updatedAt: normalizeIsoTimestamp(conversation.updatedAt),
    assignedToEmployeeId: normalizeOptionalString(conversation.assignedToEmployeeId),
    assignedToEmployeeName: normalizeOptionalString(conversation.assignedToEmployeeName),
    isAiAssistantMuted: normalizeBoolean(conversation.isAiAssistantMuted),
    isSigned: normalizeBoolean(conversation.isSigned),
  };
};

const normalizeBootstrapResponse = (value: unknown): WebWidgetBootstrapResponse => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    tenantId: normalizeRequiredString(payload.tenantId),
    tenantCode: normalizeRequiredString(payload.tenantCode),
    tenantName: normalizeRequiredString(payload.tenantName),
    widgetId: normalizeRequiredString(payload.widgetId),
    widgetName: normalizeRequiredString(payload.widgetName),
    welcomeTitle: normalizeOptionalString(payload.welcomeTitle),
    welcomeMessage: normalizeOptionalString(payload.welcomeMessage),
    appearance: normalizeAppearance(payload.appearance),
    visitorIdentity: normalizeVisitorIdentity(payload.visitorIdentity),
    session: normalizeSession(payload.session),
  };
};

const normalizeSessionState = (value: unknown): WebWidgetSessionState => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    sessionId: normalizeRequiredString(payload.sessionId),
    expiresAtUtc: normalizeIsoTimestamp(payload.expiresAtUtc),
    widgetId: normalizeRequiredString(payload.widgetId),
    widgetName: normalizeRequiredString(payload.widgetName),
    appearance: normalizeAppearance(payload.appearance),
    visitorIdentity: normalizeVisitorIdentity(payload.visitorIdentity),
    visitor: normalizeVisitor(payload.visitor),
    conversation: normalizeConversation(payload.conversation),
    typingIndicators: normalizeTypingIndicators(payload.typingIndicators),
  };
};

const normalizeMessages = (value: unknown): Message[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(item => normalizeMessage(item));
};

const normalizeMessagesPage = (value: unknown): WebWidgetSessionMessagesPage => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const conversationId = normalizeOptionalString(payload.conversationId);

  return {
    conversationId: conversationId || null,
    messages: normalizeMessages(payload.messages),
    loadedCount: normalizeOptionalNumber(payload.loadedCount) ?? 0,
    totalCount: normalizeOptionalNumber(payload.totalCount) ?? 0,
    hasMoreMessages: normalizeBoolean(payload.hasMoreMessages),
  };
};

const normalizeSessionMessageResponse = (value: unknown): WebWidgetSessionMessageResponse => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    conversationId: normalizeRequiredString(payload.conversationId),
    message: normalizeMessage(payload.message),
  };
};

const normalizeRefreshResponse = (value: unknown): WebWidgetRefreshSessionResponse => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    session: normalizeSession(payload.session),
  };
};

const normalizeRevokeResponse = (value: unknown): WebWidgetRevokeSessionResponse => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    revoked: normalizeBoolean(payload.revoked),
  };
};

const buildErrorMessage = (status: number, payload: unknown): string => {
  const envelope = payload && typeof payload === 'object' ? payload as Record<string, any> : {};
  const baseMessage = envelope.error?.message || envelope.message || `Request failed with status ${status}`;
  const details = Array.isArray(envelope.error?.details)
    ? envelope.error.details
    : Array.isArray(envelope.details)
      ? envelope.details
      : [];

  const detailMessage = details
    .map((detail: any) => {
      const field = detail?.field || detail?.propertyName || detail?.name;
      const message = detail?.issue || detail?.message || detail?.errorMessage;
      if (!message) {
        return null;
      }

      return field ? `${field}: ${message}` : String(message);
    })
    .filter(Boolean)
    .join('; ');

  return detailMessage ? `${baseMessage} ${detailMessage}` : baseMessage;
};

export const createWebWidgetClient = (config: WebWidgetClientConfig): WebWidgetClient => {
  const baseUrl = (config.baseUrl ?? API_BASE_URL).replace(/\/$/, '');
  const fetchImpl = ensureFetch(config.fetch);
  const tenantCode = config.tenantCode.trim();
  const widgetKey = config.widgetKey.trim();
  const resolvedOrigin = config.origin
    ?? (typeof window !== 'undefined' ? window.location.origin : undefined);
  const storedSession = readStoredWebWidgetSession(tenantCode, widgetKey);
  let sessionToken = config.sessionToken?.trim() || storedSession?.token || null;

  const persistSession = (session: WebWidgetSession) => {
    if (!session.token?.trim() || !session.id.trim()) {
      return;
    }

    writeStoredWebWidgetSession(tenantCode, widgetKey, {
      token: session.token.trim(),
      sessionId: session.id.trim(),
      expiresAtUtc: session.expiresAtUtc,
    });
  };

  const applySessionToken = (token: string | null | undefined) => {
    sessionToken = token?.trim() || null;

    if (!sessionToken) {
      clearStoredWebWidgetSession(tenantCode, widgetKey);
    }
  };

  const request = async <TResponse>(path: string, options: RequestOptions = {}): Promise<TResponse> => {
    const headers = new Headers();

    if (options.formData) {
      // Browser sets multipart boundary automatically.
    } else if (options.body !== undefined) {
      headers.set('Content-Type', 'application/json');
    }

    if (tenantCode) {
      headers.set(WEB_WIDGET_TENANT_HEADER, tenantCode);
    }

    if (widgetKey) {
      headers.set(WEB_WIDGET_KEY_HEADER, widgetKey);
    }

    if (sessionToken) {
      headers.set('Authorization', `Bearer ${sessionToken}`);
    }

    if (resolvedOrigin) {
      headers.set('Origin', resolvedOrigin);
    }

    const response = await fetchImpl(`${baseUrl}${WEB_WIDGET_BASE_PATH}${path}`, {
      method: options.method ?? (options.body === undefined && options.formData === undefined ? 'GET' : 'POST'),
      credentials: 'include',
      headers,
      body: options.formData ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
    });

    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      throw new Error(buildErrorMessage(response.status, payload));
    }

    return payload as TResponse;
  };

  return {
    setSessionToken: applySessionToken,
    getSessionToken: () => sessionToken,

    bootstrap: async (bootstrapRequest) => {
      const payload = await request<unknown>('/bootstrap', {
        method: 'POST',
        body: bootstrapRequest,
      });

      const response = normalizeBootstrapResponse(payload);
      applySessionToken(response.session.token);
      persistSession(response.session);
      return response;
    },

    getSessionState: async () => {
      const payload = await request<unknown>('/session/state');
      const response = normalizeSessionState(payload);

      if (sessionToken) {
        writeStoredWebWidgetSession(tenantCode, widgetKey, {
          token: sessionToken,
          sessionId: response.sessionId,
          expiresAtUtc: response.expiresAtUtc,
        });
      }

      return response;
    },

    getSessionMessages: async (query) => {
      const params = new URLSearchParams({
        loadedCount: String(query.loadedCount),
      });

      if (query.pageSize !== undefined) {
        params.set('pageSize', String(query.pageSize));
      }

      const payload = await request<unknown>(`/session/messages?${params.toString()}`);
      return normalizeMessagesPage(payload);
    },

    sendSessionMessage: async (messageRequest) => {
      const payload = await request<unknown>('/session/messages', {
        method: 'POST',
        body: messageRequest,
      });

      return normalizeSessionMessageResponse(payload);
    },

    sendSessionAttachment: async (attachmentRequest) => {
      const formData = new FormData();
      formData.append('file', attachmentRequest.file, attachmentRequest.file.name);

      if (attachmentRequest.content?.trim()) {
        formData.append('content', attachmentRequest.content.trim());
      }

      if (attachmentRequest.displayName?.trim()) {
        formData.append('displayName', attachmentRequest.displayName.trim());
      }

      if (attachmentRequest.externalVisitorId?.trim()) {
        formData.append('externalVisitorId', attachmentRequest.externalVisitorId.trim());
      }

      if (attachmentRequest.email?.trim()) {
        formData.append('email', attachmentRequest.email.trim());
      }

      if (attachmentRequest.phone?.trim()) {
        formData.append('phone', attachmentRequest.phone.trim());
      }

      const payload = await request<unknown>('/session/messages/attachment', {
        method: 'POST',
        formData,
      });

      return normalizeSessionMessageResponse(payload);
    },

    refreshSession: async () => {
      const payload = await request<unknown>('/session/refresh', {
        method: 'POST',
        body: {},
      });

      const response = normalizeRefreshResponse(payload);
      applySessionToken(response.session.token);
      persistSession(response.session);
      return response;
    },

    revokeSession: async () => {
      const payload = await request<unknown>('/session/revoke', {
        method: 'POST',
        body: {},
      });

      applySessionToken(null);
      return normalizeRevokeResponse(payload);
    },
  };
};

export default createWebWidgetClient;
